import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CalendarCheck, Loader2 } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { SITE } from "../config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const toISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const inputCls =
  "w-full rounded-2xl border border-[#064E3B]/15 bg-[#FFFDF9] px-4 py-3 text-sm text-[#1C2826] outline-none transition-colors duration-300 placeholder:text-[#526560]/50 focus:border-[#064E3B] focus:ring-2 focus:ring-[#064E3B]/15";

export default function Booking() {
  const days = useMemo(
    () =>
      Array.from({ length: 14 }, (_, i) => {
        const d = new Date();
        d.setDate(d.getDate() + i);
        return d;
      }),
    []
  );
  const [date, setDate] = useState(toISO(days[0]));
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", mode: "Online" });

  const refreshSlots = () => {
    setLoadingSlots(true);
    fetch(`${API}/slots?date=${date}`)
      .then((r) => r.json())
      .then((d) => setSlots(d.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  };

  useEffect(() => {
    setSlot(null);
    refreshSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const confirm = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/bookings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, date, time: slot }),
      });
      if (res.status === 409) {
        toast.error("That slot was just taken — please pick another time.");
        setSlot(null);
        refreshSlots();
        return;
      }
      if (!res.ok) throw new Error("failed");
      toast.success(`Booked for ${date} at ${slot}. I will confirm personally on email or WhatsApp.`);
      setForm({ name: "", email: "", phone: "", mode: "Online" });
      setSlot(null);
      refreshSlots();
    } catch {
      toast.error("Something went wrong. Please try WhatsApp instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="book" data-testid="booking-section" className="grain bg-[#F7F4EB] py-20 lg:py-28">
      <div className="relative z-[2] mx-auto grid max-w-7xl grid-cols-1 gap-14 px-4 sm:px-8 lg:grid-cols-12 lg:gap-20 lg:px-16">
        <Reveal className="lg:col-span-5">
          <Eyebrow>Book a session</Eyebrow>
          <h2 className="mt-4 font-serif text-3xl font-medium leading-snug tracking-tight text-[#064E3B] sm:text-4xl lg:text-5xl">
            Pick a time that <em className="text-[#059669]">suits</em> you
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-[#526560]">
            Sessions last {SITE.sessionLength}, online or in-person. All times are IST. Your first
            15-minute discovery call is free — book any slot and mention it. Every booking is
            confirmed personally on email or WhatsApp.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <span className="rounded-full border border-[#064E3B]/15 bg-[#FFFDF9] px-4 py-2 text-xs font-semibold text-[#064E3B]">
              Mon&ndash;Fri &middot; 5&ndash;8 PM
            </span>
            <span className="rounded-full border border-[#064E3B]/15 bg-[#FFFDF9] px-4 py-2 text-xs font-semibold text-[#064E3B]">
              Sat&ndash;Sun &middot; 10 AM&ndash;6 PM
            </span>
          </div>
        </Reveal>

        <Reveal delay={0.12} className="lg:col-span-7">
          <div className="rounded-[2rem] border border-[#064E3B]/12 bg-[#FFFDF9] p-6 shadow-[0_24px_60px_-35px_rgba(6,78,59,0.45)] sm:p-8">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.12em] text-[#064E3B]">1. Choose a day</p>
            <div data-testid="booking-date-strip" className="flex gap-2.5 overflow-x-auto pb-2">
              {days.map((d) => {
                const iso = toISO(d);
                const active = iso === date;
                return (
                  <button
                    key={iso}
                    type="button"
                    data-testid={`booking-date-${iso}`}
                    onClick={() => setDate(iso)}
                    aria-pressed={active}
                    className={`flex w-16 shrink-0 flex-col items-center rounded-2xl border px-2 py-3 transition-colors duration-300 ${
                      active
                        ? "border-[#064E3B] bg-[#064E3B] text-[#FDFBF7]"
                        : "border-[#064E3B]/15 bg-[#FDFBF7] text-[#064E3B] hover:bg-[#ECFDF5]"
                    }`}
                  >
                    <span className="text-[11px] font-medium uppercase">
                      {d.toLocaleDateString("en-IN", { weekday: "short" })}
                    </span>
                    <span className="font-serif text-xl font-semibold">{d.getDate()}</span>
                  </button>
                );
              })}
            </div>

            <p className="mt-6 mb-3 text-xs font-semibold uppercase tracking-[0.12em] text-[#064E3B]">2. Choose a time</p>
            {loadingSlots ? (
              <div className="flex items-center gap-2 py-6 text-sm text-[#526560]">
                <Loader2 size={16} className="animate-spin" /> Checking availability...
              </div>
            ) : (
              <div data-testid="booking-slot-grid" className="grid grid-cols-3 gap-2.5 sm:grid-cols-4">
                {slots.map((s) => (
                  <button
                    key={s.time}
                    type="button"
                    data-testid={`booking-slot-${s.time.replace(/[: ]/g, "-")}`}
                    disabled={!s.available}
                    onClick={() => setSlot(s.time)}
                    aria-pressed={slot === s.time}
                    className={`rounded-xl border px-2 py-2.5 text-sm font-semibold transition-colors duration-300 ${
                      !s.available
                        ? "cursor-not-allowed border-[#064E3B]/10 bg-[#F7F4EB] text-[#526560]/40 line-through"
                        : slot === s.time
                          ? "border-[#064E3B] bg-[#064E3B] text-[#FDFBF7]"
                          : "border-[#064E3B]/15 bg-[#FDFBF7] text-[#064E3B] hover:bg-[#ECFDF5]"
                    }`}
                  >
                    {s.time}
                  </button>
                ))}
              </div>
            )}

            {slot && (
              <form onSubmit={confirm} data-testid="booking-form" className="mt-7 border-t border-[#064E3B]/10 pt-6">
                <p className="mb-4 text-xs font-semibold uppercase tracking-[0.12em] text-[#064E3B]">
                  3. Your details — {date} at {slot}
                </p>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <input
                    required
                    minLength={2}
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Your name *"
                    data-testid="booking-name-input"
                    className={inputCls}
                  />
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="Email *"
                    data-testid="booking-email-input"
                    className={inputCls}
                  />
                  <input
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="Phone / WhatsApp"
                    data-testid="booking-phone-input"
                    className={inputCls}
                  />
                  <div className="flex gap-2.5" role="radiogroup" aria-label="Session mode">
                    {["Online", "In-person"].map((m) => (
                      <button
                        key={m}
                        type="button"
                        data-testid={`booking-mode-${m.toLowerCase().replace("-", "")}`}
                        onClick={() => setForm((f) => ({ ...f, mode: m }))}
                        aria-pressed={form.mode === m}
                        className={`flex-1 rounded-2xl px-4 py-3 text-sm font-semibold transition-colors duration-300 ${
                          form.mode === m
                            ? "bg-[#064E3B] text-[#FDFBF7]"
                            : "border border-[#064E3B]/20 bg-[#FDFBF7] text-[#064E3B] hover:bg-[#ECFDF5]"
                        }`}
                      >
                        {m}
                      </button>
                    ))}
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="booking-confirm-button"
                  className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-[#064E3B] px-7 py-4 text-sm font-semibold text-[#FDFBF7] transition-all duration-300 hover:bg-[#022C22] hover:shadow-[0_14px_36px_-12px_rgba(6,78,59,0.55)] disabled:opacity-60"
                >
                  {sending ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Booking...
                    </>
                  ) : (
                    <>
                      <CalendarCheck size={17} /> Confirm booking — {slot}
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
